import {Checkbox, DatePicker, Form, Select} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {DangerNote, FieldGroup, ModalShell} from './ui/ModalShell';
import theme from '../ordersTheme.module.scss';
import moment from 'moment';
import {toast} from 'react-toastify';
import {useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {AUTOMATICALLY_SETTLE_ORDERS} from 'graphql/mutations/stocks';
import {instrumentKeyOptions} from 'pages/AssetManagement/modules/instrumentKeys';
import {brokerOptionLabel} from '../brokerVenues';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';

export const OrderActionsModal = ({type, close, goBack}) => {
  const renderContent = useMemo(() => {
    if (['PORTFOLIO', 'STATEMENT', 'HISTORY'].includes(type)) {
      return <DownloadAction type={type} close={close} goBack={goBack} />;
    } else if (type === 'SETTLE') {
      return <SettleAction type={type} close={close} />;
    }
    return null;
  }, [type, close, goBack]);

  return renderContent;
};

const DownloadAction = ({type, goBack}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const {data, loading: brokerLoading} = useQuery(GET_ALL_BROKERS, {
    skip: !type,
  });
  const generateRandomId = () => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 1000);
    return `${timestamp}${random}`;
  };

  const convertToCSV = data => {
    if (!data || !Array.isArray(data)) {
      throw new Error('Invalid data format');
    }

    // Collect all unique keys, preserving order from the first object
    const headers = [...Object.keys(data[0] || {})];
    data.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!headers.includes(key)) headers.push(key);
      });
    });

    // Create CSV header row
    const csvRows = [headers.join(',')];

    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const handleDownload = values => {
    setLoading(true);
    const randomId = generateRandomId();

    // Find the selected broker from the brokers list
    const selectedBroker = data?.getBrokers?.data?.find(
      broker => broker.id === values.broker,
    );

    if (!selectedBroker) {
      toast.error('Broker not found');
      setLoading(false);
      return;
    }

    // Get the appropriate URL based on type
    const urlTemplate =
      type === 'STATEMENT'
        ? selectedBroker.statementUrl
        : selectedBroker.historyUrl;

    if (!urlTemplate) {
      toast.error(
        `${
          type === 'STATEMENT' ? 'Statement' : 'History'
        } URL not available for this broker`,
      );
      setLoading(false);
      return;
    }

    let startDate = '';
    let endDate = '';

    if (values.startDate && values.startDate !== 'Invalid date') {
      startDate = moment(values.startDate).format('YYYY-MM-DD');
    }
    if (values.endDate && values.endDate !== 'Invalid date') {
      endDate = moment(values.endDate).format('YYYY-MM-DD');
    }

    // Render the template string with values
    console.log(values.type);
    const renderedUrl = renderTemplateString(urlTemplate, {
      startDate,
      endDate,
      [type === 'STATEMENT' ? 'statementId' : 'historyId']: randomId,
      ...(type === 'STATEMENT' ? {statementType: values.type} : {}),
    });

    fetch(renderedUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        try {
          const csv = convertToCSV(data);
          const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;

          // Create filename with ID and dates
          let filename = `${type.toLowerCase()}_${randomId}`;
          if (values.startDate) {
            filename += `_from_${moment(values.startDate).format(
              'YYYY-MM-DD',
            )}`;
          }
          if (values.endDate) {
            filename += `_to_${moment(values.endDate).format('YYYY-MM-DD')}`;
          }
          filename += '.csv';

          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success('Downloaded Successfully');
        } catch (error) {
          console.error('Error converting to CSV:', error);
          toast.error('Error converting data to CSV');
        }
      })
      .catch(err => {
        console.error(err);
        toast.error('Error Downloading');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form onFinish={handleDownload} layout="vertical" form={form}>
      <ModalShell
        title={`Broker ${type === 'HISTORY' ? 'history' : 'statement'}`}
        subtitle={
          // Only brokers that actually carry a URL for this document are
          // listed, so an empty list means none is configured.
          `Downloads a CSV from the broker's ${
            type === 'HISTORY' ? 'history' : 'statement'
          } endpoint. Leave the dates empty to let the broker decide the range.`
        }
        onBack={goBack}
        footer={
          <SubmitButton
            label={loading ? 'Downloading…' : 'Download CSV'}
            disabled={loading}
          />
        }>
        <FieldGroup>
          <Form.Item
            className={theme.fieldFull}
            name="broker"
            label="Broker"
            rules={[
              {
                required: true,
                message: 'Please select a broker',
              },
            ]}>
            <Select loading={brokerLoading}>
              {data?.getBrokers?.data
                ?.filter(broker => {
                  if (type === 'STATEMENT') {
                    return broker.statementUrl;
                  }
                  if (type === 'HISTORY') {
                    return broker.historyUrl;
                  }
                  return true;
                })
                .map(item => (
                  <Select.Option key={item.id} value={item.id}>
                    {brokerOptionLabel(item)}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          {type === 'STATEMENT' && (
            <Form.Item
              className={theme.fieldFull}
              label="Type"
              name="type"
              rules={[{required: true, message: 'Please select a type'}]}>
              <Select>
                <Select.Option value="formal">
                  Formal (comes with fees)
                </Select.Option>
                <Select.Option value="informal">
                  Informal (for automated backoffice - no fees)
                </Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item label="Start Date" name="startDate">
            <DatePicker />
          </Form.Item>
          <Form.Item label="End Date" name="endDate">
            <DatePicker />
          </Form.Item>
        </FieldGroup>
      </ModalShell>
    </Form>
  );
};

const SettleAction = ({close}) => {
  const [triggerAutoSettleOrders, {loading}] = useMutation(
    AUTOMATICALLY_SETTLE_ORDERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleSettle = values => {
    triggerAutoSettleOrders({
      variables: {
        instruments: values.instruments,
      },
    })
      .then(({data: {triggerAutoSettleOrders}}) => {
        if (triggerAutoSettleOrders.__typename === 'Error') {
          toast.error(triggerAutoSettleOrders.message);
          return;
        } else {
          toast.success(triggerAutoSettleOrders.message);
          close();
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  return (
    <Form onFinish={handleSettle} layout="vertical">
      <ModalShell
        title="Settle orders automatically"
        subtitle="Every eligible pending order for the instruments you choose is settled immediately."
        footer={
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => (
              <SubmitButton
                disabled={loading || !getFieldValue('confirm')}
                label={loading ? 'Settling…' : 'Settle orders'}
              />
            )}
          </Form.Item>
        }>
        <DangerNote>
          This cannot be undone. Settlement is applied across the whole book for
          the chosen instruments, not just the orders on screen.
        </DangerNote>
        <FieldGroup>
          <Form.Item
            className={theme.fieldFull}
            rules={[
              {
                required: true,
                message: 'Please select at least one instrument',
              },
            ]}
            label="Instruments"
            name="instruments">
            <Select
              // `triggerAutoSettleOrders` takes `[InstrumentKey!]`, and the
              // pre-v2 `STOCK` this offered is not a member of that enum.
              options={instrumentKeyOptions(['PUBLIC_EQUITY', 'MUTUAL_FUND'])}
              allowClear
              placeholder="Choose one or more instruments to settle now."
              showSearch
              optionFilterProp="label"
              mode="multiple"
            />
          </Form.Item>
          <Form.Item
            className={`${theme.fieldFull} ${theme.consentField}`}
            name="confirm"
            valuePropName="checked"
            rules={[
              {
                async validator(_, v) {
                  if (v) return;
                  throw new Error('You must confirm this irreversible action.');
                },
              },
            ]}>
            <Checkbox>I understand this action cannot be reversed.</Checkbox>
          </Form.Item>
        </FieldGroup>
      </ModalShell>
    </Form>
  );
};

export function renderTemplateString(template, data) {
  return template.replace(/{([^}]+)}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(data, key) ? data[key] : match,
  );
}
